import { inject } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { LocalStorageService } from './storage';
import { ListConfig, ListMetadata } from '../core/types/list.types';

// Dokument-Präfix für Listen in CouchDB
const LIST_DOC_PREFIX = 'list_';
// LocalStorage-Fallback-Präfix
const STORAGE_PREFIX = 'userList_';

/**
 * Abstrakte Basis für Listenverwaltungs-Services.
 * Bietet Multi-Tier-Caching (Memory → LocalStorage → CouchDB → Defaults)
 * und bidirektionale Synchronisierung.
 *
 * Konkrete Services müssen `editableLists`, `getRemoteDb()` und `getCurrentUser()` implementieren.
 */
export abstract class BaseListenService {
  protected ls = inject(LocalStorageService);

  protected remoteDb: any | null = null;
  protected dbReady: Promise<void> = Promise.resolve();

  // Memory-Cache für schnellen Zugriff
  private memoryCache = new Map<string, any[]>();

  // Observable für Verbindungsstatus
  protected storageType$ = new BehaviorSubject<'database' | 'local'>('local');

  /** Konfiguration der editierbaren Listen — von der App zu füllen */
  abstract readonly editableLists: ListConfig[];

  /** Gibt die Remote-DB-Instanz zurück (oder null wenn nicht verbunden) */
  protected abstract getRemoteDb(): any | null;

  /** Gibt den aktuellen Benutzernamen zurück */
  protected abstract getCurrentUser(): string;

  private getDocId(listName: string): string {
    return LIST_DOC_PREFIX + listName;
  }

  /**
   * Lädt eine Liste über 4-stufige Fallback-Kette:
   * 1. Memory-Cache → 2. LocalStorage → 3. CouchDB → 4. Default-JSON
   */
  async getList(listName: string): Promise<any[] | null> {
    const config = this.editableLists.find(l => l.name === listName);
    if (!config) return null;

    // 1. Memory-Cache
    const cached = this.memoryCache.get(listName);
    if (cached) return cached;

    // 2. LocalStorage
    const storageData = await this.ls.get(STORAGE_PREFIX + listName);
    if (storageData) {
      let data: any[];
      if (typeof storageData === 'object' && 'data' in storageData) {
        data = (storageData as any).data as any[];
      } else {
        data = storageData as any[];
      }
      this.memoryCache.set(listName, data);
      return data;
    }

    // 3. CouchDB
    await this.dbReady;
    const db = this.getRemoteDb();
    if (db) {
      try {
        const doc = await db.get(this.getDocId(listName));
        if (doc && doc.data) {
          this.memoryCache.set(listName, doc.data);
          await this.ls.set(STORAGE_PREFIX + listName, {
            data: doc.data,
            updatedAt: doc.updatedAt,
            updatedBy: doc.updatedBy
          });
          return doc.data as any[];
        }
      } catch (e: any) {
        if (e.status !== 404) {
          console.error('Fehler beim Laden der Liste aus DB:', e);
        }
      }
    }

    // 4. Default-Daten
    this.memoryCache.set(listName, config.defaultData);
    return [...config.defaultData];
  }

  /**
   * Speichert eine Liste in Memory-Cache, LocalStorage und optional CouchDB.
   */
  async saveList(listName: string, data: any[]): Promise<void> {
    await this.dbReady;
    const currentUser = this.getCurrentUser();
    const now = new Date().toISOString();

    // Memory-Cache aktualisieren
    this.memoryCache.set(listName, data);

    // LocalStorage
    const storageData = {
      data: data,
      updatedAt: now,
      updatedBy: currentUser
    };
    await this.ls.set(STORAGE_PREFIX + listName, storageData);

    const db = this.getRemoteDb();
    if (db) {
      const docId = this.getDocId(listName);
      try {
        const existingDoc = await db.get(docId).catch(() => null);
        const doc: any = {
          _id: docId,
          type: 'list',
          listName: listName,
          data: data,
          updatedAt: now,
          updatedBy: currentUser
        };
        if (existingDoc) {
          doc._rev = existingDoc._rev;
        }
        await db.put(doc);
        console.log('Liste in CouchDB gespeichert:', listName);
      } catch (e) {
        console.error('Fehler beim Speichern der Liste in CouchDB:', e);
        throw e;
      }
    }
  }

  /**
   * Setzt eine Liste auf die Standardwerte zurück.
   */
  async resetList(listName: string): Promise<any[] | null> {
    const config = this.editableLists.find(l => l.name === listName);
    if (!config) return null;

    await this.dbReady;

    // Memory-Cache leeren
    this.memoryCache.delete(listName);

    const db = this.getRemoteDb();
    if (db) {
      try {
        const doc = await db.get(this.getDocId(listName));
        if (doc) {
          await db.remove(doc);
        }
      } catch (e: any) {
        if (e.status !== 404) {
          console.error('Fehler beim Löschen der Liste aus DB:', e);
        }
      }
    }

    // LocalStorage löschen
    await this.ls.remove(STORAGE_PREFIX + listName);

    return [...config.defaultData];
  }

  /**
   * Prüft ob eine benutzerdefinierte Version der Liste existiert.
   */
  async hasCustomList(listName: string): Promise<boolean> {
    await this.dbReady;

    const db = this.getRemoteDb();
    if (db) {
      try {
        const doc = await db.get(this.getDocId(listName));
        return !!doc;
      } catch (e) {
        return false;
      }
    }

    const storageData = await this.ls.get(STORAGE_PREFIX + listName);
    return storageData !== null;
  }

  getListConfig(listName: string): ListConfig | undefined {
    return this.editableLists.find(l => l.name === listName);
  }

  isConnected(): boolean {
    return !!this.getRemoteDb();
  }

  getStorageType(): 'database' | 'local' | 'none' {
    if (this.getRemoteDb()) return 'database';
    return 'local';
  }

  watchStorageType(): Observable<'database' | 'local'> {
    return this.storageType$.asObservable();
  }

  async getListMetadata(listName: string): Promise<ListMetadata | null> {
    await this.dbReady;

    const db = this.getRemoteDb();
    if (db) {
      try {
        const doc = await db.get(this.getDocId(listName));
        if (doc) {
          return {
            updatedAt: doc.updatedAt || '',
            updatedBy: doc.updatedBy || ''
          };
        }
      } catch (e) {
        return null;
      }
    }

    const storageData = await this.ls.get(STORAGE_PREFIX + listName);
    if (storageData && typeof storageData === 'object' && 'updatedAt' in storageData) {
      return {
        updatedAt: (storageData as any).updatedAt || '',
        updatedBy: (storageData as any).updatedBy || ''
      };
    }
    return null;
  }

  /**
   * Cached alle Listen von der DB für Offline-Nutzung (ein Bulk-Request).
   */
  protected async cacheListsForOffline(): Promise<void> {
    const db = this.getRemoteDb();
    if (!db) return;

    try {
      const docIds = this.editableLists.map(l => this.getDocId(l.name));
      const result = await db.allDocs({ keys: docIds, include_docs: true });

      let cachedCount = 0;
      for (const row of result.rows as any[]) {
        if ('error' in row) continue;
        if (row.doc && row.doc.data) {
          const doc = row.doc;
          const listName = doc.listName || row.id.replace(LIST_DOC_PREFIX, '');
          await this.ls.set(STORAGE_PREFIX + listName, {
            data: doc.data,
            updatedAt: doc.updatedAt,
            updatedBy: doc.updatedBy
          });
          this.memoryCache.set(listName, doc.data);
          cachedCount++;
        }
      }
      if (cachedCount > 0) {
        console.log(`ListenService: ${cachedCount} Listen gecacht`);
      }
    } catch (e: any) {
      console.error('Fehler beim Bulk-Cachen der Listen:', e);
    }
  }

  /**
   * Synchronisiert lokale Listen mit der Datenbank.
   * Strategie: Neuere Version gewinnt.
   */
  protected async syncLocalListsToDb(): Promise<void> {
    const db = this.getRemoteDb();
    if (!db) return;

    let syncedCount = 0;
    for (const listConfig of this.editableLists) {
      const storageData = await this.ls.get(STORAGE_PREFIX + listConfig.name);
      const docId = this.getDocId(listConfig.name);

      let dbDoc: any = null;
      try {
        dbDoc = await db.get(docId);
      } catch (e) { /* Dokument existiert nicht */ }

      if (storageData) {
        let localData: any[];
        let localUpdatedAt: string;
        let localUpdatedBy: string;

        if (typeof storageData === 'object' && 'data' in storageData) {
          localData = (storageData as any).data;
          localUpdatedAt = (storageData as any).updatedAt || '';
          localUpdatedBy = (storageData as any).updatedBy || 'Unbekannt';
        } else {
          localData = storageData as any[];
          localUpdatedAt = '';
          localUpdatedBy = 'Unbekannt';
        }

        if (dbDoc) {
          const dbUpdatedAt = dbDoc.updatedAt || '';
          if (localUpdatedAt > dbUpdatedAt) {
            try {
              await db.put({
                _id: docId, _rev: dbDoc._rev,
                type: 'list', listName: listConfig.name,
                data: localData, updatedAt: localUpdatedAt,
                updatedBy: localUpdatedBy, syncedFromLocal: true
              });
              syncedCount++;
            } catch (e) {
              console.error(`Fehler beim Hochladen der Liste "${listConfig.name}":`, e);
            }
          } else if (dbUpdatedAt > localUpdatedAt) {
            await this.ls.set(STORAGE_PREFIX + listConfig.name, {
              data: dbDoc.data, updatedAt: dbDoc.updatedAt, updatedBy: dbDoc.updatedBy
            });
          }
        } else {
          try {
            await db.put({
              _id: docId, type: 'list', listName: listConfig.name,
              data: localData, updatedAt: localUpdatedAt || new Date().toISOString(),
              updatedBy: localUpdatedBy, syncedFromLocal: true
            });
            syncedCount++;
          } catch (e) {
            console.error(`Fehler beim Hochladen der Liste "${listConfig.name}":`, e);
          }
        }
      } else if (dbDoc) {
        await this.ls.set(STORAGE_PREFIX + listConfig.name, {
          data: dbDoc.data, updatedAt: dbDoc.updatedAt, updatedBy: dbDoc.updatedBy
        });
      }
      await new Promise(resolve => setTimeout(resolve, 300));
    }

    if (syncedCount > 0) {
      console.log(`${syncedCount} lokale Listen zur Datenbank synchronisiert`);
    }
  }
}
